import { useEffect } from 'react';
import { create } from 'zustand';

/**
 * How many modal-style overlays (Modal, FullScreenSheet, the mobile drawer)
 * are open right now.
 *
 * Exists for the bottom toast slot (YAR-476): its clock counts *visible*
 * time, so it has to pause while anything covers the toast. A counter rather
 * than a boolean because overlays nest — a confirmation Modal opened from a
 * FullScreenSheet must not resume the clock when the inner one closes.
 */
interface OverlayState {
  openCount: number;
  push: () => void;
  pop: () => void;
}

export const useOverlayStore = create<OverlayState>()((set) => ({
  openCount: 0,
  push: () => set((s) => ({ openCount: s.openCount + 1 })),
  // Clamped: an unbalanced pop (a component unmounted twice under
  // StrictMode, say) must never drive the count negative and leave the clock
  // believing nothing is open while something is.
  pop: () => set((s) => ({ openCount: Math.max(0, s.openCount - 1) })),
}));

/**
 * Count this component as an open overlay while `open` is true. Balanced by
 * the effect cleanup, so unmounting while open releases it too.
 */
export function useRegisterOverlay(open: boolean) {
  useEffect(() => {
    if (!open) return;
    useOverlayStore.getState().push();
    return () => useOverlayStore.getState().pop();
  }, [open]);
}
